import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, X, AlertTriangle } from "lucide-react";

export function Spinner({ className = "" }) {
  return <Loader2 className={`animate-spin text-violet-400 ${className}`} />;
}

// Shows text clamped to `lines`; if the content overflows, reveals a
// "View more"/"View less" toggle so full descriptions and dice notation are
// always readable without permanently taking up space.
export function ExpandableText({ text, lines = 3, className = "" }) {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (el) setOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [text, expanded]);

  if (!text) return null;

  const clampStyle = expanded
    ? undefined
    : { display: "-webkit-box", WebkitLineClamp: lines, WebkitBoxOrient: "vertical", overflow: "hidden" };

  return (
    <div>
      <p ref={ref} className={className} style={clampStyle}>{text}</p>
      {(overflowing || expanded) && (
        <button
          type="button"
          data-testid="expand-text-btn"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExpanded((v) => !v); }}
          className="mt-1 text-xs font-semibold text-violet-300 hover:text-violet-200 transition"
        >
          {expanded ? "View less" : "View more"}
        </button>
      )}
    </div>
  );
}

export function PageLoader() {
  return (
    <div className="flex items-center justify-center py-32">
      <Spinner className="w-8 h-8" />
    </div>
  );
}

export function PageHeader({ title, subtitle, icon: Icon, action }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-7 animate-float-up">
      <div className="flex items-center gap-3.5">
        {Icon && (
          <div className="w-12 h-12 rounded-2xl glass flex items-center justify-center text-violet-300">
            <Icon size={22} />
          </div>
        )}
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-wide text-white">{title}</h1>
          {subtitle && <p className="text-sm text-zinc-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="glass rounded-3xl py-16 px-6 flex flex-col items-center text-center animate-float-up">
      {Icon && (
        <div className="w-16 h-16 rounded-2xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center text-violet-300 mb-4">
          <Icon size={28} />
        </div>
      )}
      <h3 className="font-display text-xl text-white">{title}</h3>
      {subtitle && <p className="text-sm text-zinc-500 mt-1 max-w-sm">{subtitle}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function Modal({ open, onClose, title, children, size = "md" }) {
  const widths = { sm: "max-w-md", md: "max-w-lg", lg: "max-w-2xl", xl: "max-w-4xl" };
  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/75 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ type: "spring", damping: 24, stiffness: 280 }}
            className={`relative w-full ${widths[size]} my-8 rounded-3xl border border-white/10 bg-[#0d0d13] shadow-2xl`}
            data-testid="modal"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06]">
              <h3 className="font-display text-lg font-semibold text-white">{title}</h3>
              <button onClick={onClose} className="p-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5" data-testid="modal-close">
                <X size={18} />
              </button>
            </div>
            <div className="p-6">{children}</div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-zinc-400 mb-1.5 block uppercase tracking-wide">{label}</span>
      {children}
    </label>
  );
}

export function TextInput(props) {
  return <input {...props} className={`input-dark w-full rounded-xl px-3.5 py-2.5 text-sm ${props.className || ""}`} />;
}

export function Btn({ children, variant = "primary", className = "", ...props }) {
  const styles = {
    primary: "btn-violet",
    ghost: "glass glass-hover text-zinc-200",
    danger: "bg-rose-500/15 text-rose-300 border border-rose-500/30 hover:bg-rose-500/25",
  };
  return (
    <button
      {...props}
      className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition ${styles[variant]} ${className}`}
    >
      {children}
    </button>
  );
}

export function ConfirmDialog({ open, onClose, onConfirm, title = "Are you sure?", message = "This action can't be undone.", confirmLabel = "Delete" }) {
  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="confirm-dialog">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="fixed inset-0 bg-black/75" />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 12 }}
            transition={{ type: "spring", damping: 24, stiffness: 280 }}
            className="relative w-full max-w-sm rounded-3xl border border-white/10 bg-[#0d0d13] p-6 shadow-2xl"
          >
            <div className="w-12 h-12 rounded-2xl bg-rose-500/15 text-rose-300 flex items-center justify-center mb-4"><AlertTriangle size={22} /></div>
            <h3 className="font-display text-lg text-white mb-1">{title}</h3>
            <p className="text-sm text-zinc-400 mb-5">{message}</p>
            <div className="flex gap-3">
              <Btn variant="ghost" className="flex-1" onClick={onClose} data-testid="confirm-cancel">Cancel</Btn>
              <Btn variant="danger" className="flex-1" onClick={onConfirm} data-testid="confirm-ok">{confirmLabel}</Btn>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

// A button that asks for confirmation (centered popout) before running onConfirm.
export function ConfirmButton({ onConfirm, title, message, confirmLabel, className = "", children, ...props }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        className={className}
        {...props}
      >
        {children}
      </button>
      <ConfirmDialog
        open={open}
        title={title}
        message={message}
        confirmLabel={confirmLabel}
        onClose={() => setOpen(false)}
        onConfirm={() => { setOpen(false); onConfirm(); }}
      />
    </>
  );
}
